// @ts-check
/**
 * Shared CDM RawView Component
 *
 * Renders the drill-down view for every CDM type (CRM, HRIS, Finance,
 * Procurement) as grouped, labeled key-value pairs — replacing the old
 * per-domain JSON-dump stubs.
 *
 * The data is the enriched-view row already carried on `reference.metadata`
 * (the same payload the references table reads), so there is no fetch — the
 * view renders synchronously.
 *
 * Layout is driven entirely by the type's display_spec from ToolsV2Metadata,
 * which maps enriched-view column name → FieldDisplayInfo
 * ({ display_label, priority, section, display }):
 *   - Fields with display === false are dropped.
 *   - The sys_identifier field (the internal id) is never shown.
 *   - Fields with no section (null) render in the primary section, first.
 *   - Fields with a section render under a named sub-section heading; sections
 *     appear alphabetically by name after the primary section.
 *   - Within every section, fields render alphabetically by display_label.
 *
 * Cell values use the same light pretty-printing heuristics as the references
 * view (ISO dates → formatDate, booleans → Yes/No, large numbers → separators).
 */

/** @typedef {import('../../../../stores/referenceUnitStore').ReferenceUnitHandle} ReferenceUnitHandle */
/** @typedef {import('../../../../stores/referenceUnitStore').FieldDisplayInfo} FieldDisplayInfo */

/**
 * @param {any} deps
 */
export default function createComponent(deps) {
    const { computed, useReferenceUnitStore, formatDate } = deps;

    return {
        name: 'CdmRawView',

        props: {
            reference: {
                type: Object,
                required: true,
            },
        },

        /** @param {{ reference: ReferenceUnitHandle }} props */
        setup(props) {
            const referenceUnitStore = useReferenceUnitStore();

            /**
             * Pretty-print a single enriched-view cell. Mirrors the references
             * view's formatValue so list and detail render values identically.
             * @param {unknown} val
             */
            const formatValue = (val) => {
                if (val === null || val === undefined || val === '') return '';
                if (typeof val === 'boolean') return val ? 'Yes' : 'No';
                if (typeof val === 'number') {
                    return Math.abs(val) > 100 ? val.toLocaleString() : String(val);
                }
                if (typeof val === 'string') {
                    if (/^\d{4}-\d{2}-\d{2}/.test(val)) {
                        const formatted = formatDate(val);
                        return formatted || val;
                    }
                    return val;
                }
                return String(val);
            };

            const typeName = computed(() => props.reference?.type || '');

            /**
             * Build the ordered list of sections from the display_spec and the
             * reference metadata. The primary (null-section) group is first,
             * then named sub-sections alphabetically; fields within each are
             * alphabetical by display_label.
             *
             * @returns {{ name: string | null, rows: { label: string, value: string, supported: boolean }[] }[]}
             */
            const sections = computed(() => {
                const spec = referenceUnitStore.getDisplaySpec(typeName.value) || {};
                const metadata = props.reference?.metadata || {};

                /** @type {Map<string | null, { label: string, value: string, supported: boolean }[]>} */
                const bySection = new Map();

                for (const [colName, info] of Object.entries(spec)) {
                    /** @type {FieldDisplayInfo} */
                    const fieldInfo = info;
                    if (fieldInfo.display === false) continue;
                    // The internal id is wiring, never surfaced.
                    if (fieldInfo.priority === 'sys_identifier') continue;

                    const isSupported = fieldInfo.supported !== false;
                    const sectionName = fieldInfo.section ?? null;
                    const row = {
                        label: fieldInfo.display_label || colName,
                        value: isSupported ? formatValue(metadata[colName]) : '',
                        supported: isSupported,
                    };

                    if (!bySection.has(sectionName)) {
                        bySection.set(sectionName, []);
                    }
                    /** @type {{ label: string, value: string, supported: boolean }[]} */ (
                        bySection.get(sectionName)
                    ).push(row);
                }

                // Sort fields alphabetically by label within each section.
                for (const rows of bySection.values()) {
                    rows.sort((a, b) => a.label.localeCompare(b.label));
                }

                // Primary (null) section first, then named sections A→Z.
                const named = [...bySection.keys()]
                    .filter((s) => s !== null)
                    .sort((a, b) =>
                        /** @type {string} */ (a).localeCompare(/** @type {string} */ (b)),
                    );

                /** @type {{ name: string | null, rows: { label: string, value: string }[] }[]} */
                const ordered = [];
                if (bySection.has(null)) {
                    ordered.push({ name: null, rows: bySection.get(null) || [] });
                }
                for (const name of named) {
                    ordered.push({ name, rows: bySection.get(name) || [] });
                }
                return ordered;
            });

            const hasContent = computed(() =>
                sections.value.some(
                    /** @param {{ rows: unknown[] }} s */ (s) => s.rows.length > 0,
                ),
            );

            return {
                typeName,
                sections,
                hasContent,
            };
        },

        template: `
            <div class="cdm-raw-view">
                <div v-if="hasContent" class="cdm-raw-view-details">
                    <div
                        v-for="section in sections"
                        :key="section.name || '__primary__'"
                        class="cdm-raw-view-section"
                    >
                        <div v-if="section.name" class="cdm-raw-view-section-header">
                            {{ section.name }}
                        </div>
                        <div class="cdm-raw-view-grid">
                            <div
                                v-for="row in section.rows"
                                :key="row.label"
                                :class="['cdm-raw-view-row', { 'cdm-raw-view-row--unsupported': !row.supported }]"
                            >
                                <span class="cdm-raw-view-label">{{ row.label }}</span>
                                <span v-if="row.supported" class="cdm-raw-view-value">{{ row.value }}</span>
                                <span v-else class="cdm-raw-view-value">Not provided by available data</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="cdm-raw-view-empty">
                    No details available for this record.
                </div>
            </div>
        `,
    };
}
